/**
 * 사진 로컬 저장소 작업
 * IndexedDB photos 스토어에 대한 CRUD 함수
 */

import {
  getRecord,
  putRecord,
  deleteRecord,
  clearStore,
  getRecordsByIndex,
  getAllRecords,
} from "./index";
import type { LocalPhoto } from "./types";

const STORE_NAME = "photos";

/**
 * 로컬 사진 ID 생성
 */
function generatePhotoId(): string {
  return `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * 사진을 로컬 저장소에 저장
 * @param photo 저장할 사진 데이터 (id, createdAt, syncStatus 제외)
 * @returns 생성된 사진 ID
 */
export async function saveLocalPhoto(
  photo: Omit<LocalPhoto, "id" | "createdAt" | "syncStatus">
): Promise<string> {
  const id = generatePhotoId();
  const localPhoto: LocalPhoto = {
    ...photo,
    id,
    createdAt: Date.now(),
    syncStatus: "pending",
  };
  await putRecord(STORE_NAME, localPhoto);
  return id;
}

/**
 * ID로 로컬 사진 조회
 * @param id 사진 ID
 * @returns 사진 데이터 또는 undefined
 */
export async function getLocalPhoto(id: string): Promise<LocalPhoto | undefined> {
  return getRecord<LocalPhoto>(STORE_NAME, id);
}

/**
 * 설비별 로컬 사진 조회 (by-machineId 인덱스 사용)
 * @param machineId 설비 복합 키 ({projectId}_{machineId})
 * @returns 해당 설비의 사진 목록
 */
export async function getLocalPhotosByMachineId(machineId: string): Promise<LocalPhoto[]> {
  return getRecordsByIndex<LocalPhoto>(STORE_NAME, "by-machineId", machineId);
}

/**
 * 설비별 로컬 사진 조회 (하위 호환용)
 * @deprecated getLocalPhotosByMachineId 사용 권장
 */
export async function getLocalPhotosByMachine(
  projectId: string,
  _docKey: string,
  machineId: string
): Promise<LocalPhoto[]> {
  // machineId 형식: {projectId}_{machineId}
  const compositeKey = `${projectId}_${machineId}`;
  return getLocalPhotosByMachineId(compositeKey);
}

/**
 * 동기화 대기 중인 사진 조회 (synced=false)
 * @returns 동기화 대기 중인 사진 목록
 */
export async function getPendingPhotos(): Promise<LocalPhoto[]> {
  // IndexedDB는 boolean 인덱스를 지원하지 않으므로 전체 조회 후 필터링
  const allPhotos = await getAllRecords<LocalPhoto>(STORE_NAME);
  return allPhotos.filter((photo) => !photo.synced);
}

/**
 * 사진 동기화 상태 업데이트
 */
export async function updatePhotoSyncStatus(
  id: string,
  syncStatus: LocalPhoto["syncStatus"],
  serverUrl?: string
): Promise<void> {
  const photo = await getLocalPhoto(id);
  if (photo) {
    await putRecord(STORE_NAME, {
      ...photo,
      syncStatus,
      synced: syncStatus === "synced",
      serverUrl: serverUrl ?? photo.serverUrl,
      uploadedAt: syncStatus === "synced" ? Date.now() : photo.uploadedAt,
    });
  }
}

/**
 * 로컬 사진 업데이트 (일반)
 * @param id 사진 ID
 * @param updates 업데이트할 필드
 */
export async function updateLocalPhoto(id: string, updates: Partial<LocalPhoto>): Promise<void> {
  const photo = await getLocalPhoto(id);
  if (photo) {
    await putRecord(STORE_NAME, { ...photo, ...updates });
  }
}

/**
 * 로컬 사진 삭제
 */
export async function removeLocalPhoto(id: string): Promise<void> {
  await deleteRecord(STORE_NAME, id);
}

/**
 * 로컬 사진 삭제 (alias)
 */
export async function deleteLocalPhoto(id: string): Promise<void> {
  await deleteRecord(STORE_NAME, id);
}

/**
 * 동기화 완료된 사진 정리 (메타데이터 삭제)
 * 로컬 파일은 sync.ts에서 별도로 삭제함
 * @returns 삭제된 사진 수
 */
export async function cleanupSyncedPhotos(): Promise<number> {
  // IndexedDB는 boolean 인덱스를 지원하지 않으므로 전체 조회 후 필터링
  const allPhotos = await getAllRecords<LocalPhoto>(STORE_NAME);
  const syncedPhotos = allPhotos.filter((photo) => photo.synced);
  let count = 0;
  for (const photo of syncedPhotos) {
    // 서버에 업로드된 사진의 메타데이터 삭제
    if (photo.serverUrl) {
      await deleteRecord(STORE_NAME, photo.id);
      count++;
    }
  }
  return count;
}

/**
 * 모든 로컬 사진 삭제
 */
export async function clearPhotoCache(): Promise<void> {
  await clearStore(STORE_NAME);
}

/**
 * 모든 로컬 사진 조회
 * @returns 모든 사진 목록
 */
export async function getAllLocalPhotos(): Promise<LocalPhoto[]> {
  return getAllRecords<LocalPhoto>(STORE_NAME);
}

/**
 * 프로젝트별 로컬 사진 목록 조회
 * @param projectId 프로젝트 ID
 * @returns 해당 프로젝트의 사진 목록
 */
export async function getLocalPhotosByProject(projectId: string): Promise<LocalPhoto[]> {
  const allPhotos = await getAllLocalPhotos();
  return allPhotos.filter((photo) => photo.projectId === projectId);
}

/**
 * 설비 삭제 시 해당 설비의 로컬 사진 메타데이터 일괄 삭제 (#457)
 * @returns 삭제된 사진 수
 */
export async function cleanupEquipmentPhotos(
  projectId: string,
  machineId: string
): Promise<number> {
  const compositeKey = `${projectId}_${machineId}`;
  const photos = await getLocalPhotosByMachineId(compositeKey);
  let count = 0;
  for (const photo of photos) {
    await deleteRecord(STORE_NAME, photo.id);
    count++;
  }
  return count;
}
